import React, { FC, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  fetchPlaylistById,
  selectIsPlaylistIdInProgress,
  selectPlaylistById,
} from '@store/slices/playlist.slice'
import {
  AISlotModel,
  fetchAllAiSlots,
  selectAiSlotById,
  selectAiSlotLoading,
  selectAiSlotsByIds,
} from '@store/slices/aiSlot.slice'
import { DjangoFilter } from '@store/slices/generic.slice'
import { Col, ListGroup } from 'react-bootstrap'
import { toHourMinString } from '@shared/tools/time.tools'
import { Slot } from '@shared/classes'
import {
  AIMetadataModel,
  selectAIMetadataById,
  selectAIMetadataLoading,
} from '@store/slices/aiMeta.slice'
import { fetchAllMetadataBySlotIds } from '@store/thunk/aimeta.thunk'
import { getSlotStateName } from '@shared/tools/enum.tools'
import { useTheme } from '@config/theme.config'

const PlaylistComponent: FC = () => {
  const { playlistId } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const { theme } = useTheme()

  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const playlistLoading = useSelector((state: RootState) =>
    playlistId ? selectIsPlaylistIdInProgress(state, playlistId) : null
  )

  const aiSlotsLoading = useSelector(selectAiSlotLoading)
  const aiSlots: AISlotModel[] = useSelector((state: RootState) =>
    playlist ? selectAiSlotsByIds(state, playlist.aiSlots) : undefined
  )

  const [aiSlotsOnly, setAiSlotsOnly] = useState(false)

  const aiMetadataLoading = useSelector(selectAIMetadataLoading)

  useEffect(() => {
    if (!aiMetadataLoading && playlist?.aiSlots)
      dispatch(fetchAllMetadataBySlotIds(playlist?.aiSlots))
  }, [playlist?.aiSlots])

  useEffect(() => {
    if (playlistId && !playlist && !playlistLoading)
      dispatch(fetchPlaylistById(playlistId))
  }, [playlistId])

  useEffect(() => {
    if (
      playlist &&
      !aiSlotsLoading &&
      aiSlots.length !== playlist.aiSlots.length
    )
      fetchSlotsForPlaylist()
  }, [playlist])

  const fetchSlotsForPlaylist = () => {
    const filter = new DjangoFilter<AISlotModel>()
    playlist && filter.addIdFilter(playlist.aiSlots)
    dispatch(fetchAllAiSlots(filter))
    playlist && dispatch(fetchAllMetadataBySlotIds(playlist.aiSlots))
  }

  // Playlist component details here
  return (
    <>
      {playlistId && (
        <div className={'mx-auto'}>
          <Col>
            <ListGroup>
              <ListGroup.Item active>
                Pouze AI sloty&emsp;
                <input
                  type="checkbox"
                  checked={aiSlotsOnly}
                  onChange={() => setAiSlotsOnly(!aiSlotsOnly)}
                />
              </ListGroup.Item>
              <div
                style={{
                  maxHeight: '700px',
                  scrollbarWidth: 'thin',
                  border:
                    theme === 'light'
                      ? '1px solid #ced4da'
                      : '1px solid #495057',
                }}
                className="overflow-y-scroll"
              >
                {playlist?.items &&
                  JSON.parse(playlist.items).map(
                    (slot: Slot) =>
                      (!aiSlotsOnly || slot.aiSlotId) && (
                        <SlotRowComponent
                          slot={slot}
                          aiSlotsOnly={aiSlotsOnly}
                          key={
                            slot.plid
                              ? slot.plid
                              : slot.element
                                ? slot.element.elementId
                                : slot.sqTime
                          }
                        ></SlotRowComponent>
                      )
                  )}
              </div>
            </ListGroup>
          </Col>
        </div>
      )}
    </>
  )
}

const SlotRowComponent: FC<{
  slot: Slot
  aiSlotsOnly: boolean
}> = ({ slot, aiSlotsOnly }) => {
  const { slotId, playlistId } = useParams()
  const attributes = slot.element.attributes
  const title = attributes.find((attribute) => attribute.id === 'Title')
  const artist = attributes.find((attribute) => attribute.id === 'Artist')
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slot.aiSlotId ? selectAiSlotById(state, slot.aiSlotId) : null
  )
  const aiMetadata: AIMetadataModel | null = useSelector((state: RootState) =>
    aiSlot?.aiEntryMetadata
      ? selectAIMetadataById(state, aiSlot?.aiEntryMetadata)
      : null
  )

  return (
    <>
      {aiSlot ? (
        <ListGroup.Item
          as={Link}
          to={`/playlist/${playlistId}/slot/${aiSlot?.id}`}
          action
          active={!aiSlotsOnly}
          disabled={aiSlot?.id == slotId}
          className={
            aiSlot?.id == slotId ? 'bg-secondary border-secondary' : ''
          }
        >
          <div className={'d-flex justify-content-between'}>
            <div>
              <span>{toHourMinString(slot.startTime)}</span>
              <span className="ms-2">
                <strong>{slot.element.eTypeName}</strong>
              </span>
              <strong>
                {' '}
                - {getSlotStateName(aiSlot?.state).toUpperCase()}
              </strong>
            </div>
            <div>
              {aiMetadata && <span>{aiMetadata.aiLinkType.toUpperCase()}</span>}
            </div>
          </div>
        </ListGroup.Item>
      ) : (
        <ListGroup.Item>
          <div className={'d-flex justify-content-between'}>
            <div>
              <span>{toHourMinString(slot.startTime)}</span>
              <span className="ms-2">
                <strong>{slot.element.eTypeName}</strong>
              </span>
            </div>
            <div>
              {artist && <span>{artist.value}: </span>}
              {title && <span>{title.value}</span>}
            </div>
          </div>
        </ListGroup.Item>
      )}
    </>
  )
}

export default PlaylistComponent
